import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  ClipboardCheck, ShoppingCart, ArrowUpCircle, RotateCcw, SlidersHorizontal, Pencil,
} from 'lucide-react'
import apiClient from '@/api/client'
import type { StockItem, StockMovement } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetBody,
} from '@/components/ui/sheet'
import { formatDateTimeHuman } from '@/lib/utils'
import { fmtQty, fmtQtyUnit, isNearIntegerArtifact, QTY_ARTIFACT_HINT } from '@/lib/quantity'
import { itemStatusMeta, contextAction } from './itemStatus'
import { describeMovement, MOVEMENT_TONE_CLS } from './describeMovement'
import { ReorderLevelPopover } from './ReorderLevelPopover'
import { canAccessAdminRoute, getStoredUserRole } from '@/lib/staff-roles'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { ItemBarcodesSection, PurchaseUnitLine } from './ItemMasterSections'
import { ItemLotsPanel } from './ItemLotsPanel'
import { PrepSection } from './prep/PrepSection'
import { ParLevelsCard } from '../purchasing/ParLevelsCard'
import { PriceHistoryCard } from '../purchasing/PriceHistoryCard'

export type ItemDrawerAction = 'count' | 'purchase' | 'issue' | 'supplierReturn' | 'adjust' | 'editItem'

/**
 * The item's own page (F-FE-N103): identity, live status, both costs, and its
 * movement history — the "what happened to THIS item" question that used to
 * require paging a global feed. Opens from any Items row (and deep links via
 * ?item=<id>); actions hand back to the dashboard's modal switch.
 */
export function ItemDrawer({ itemId, onClose, onAction, onViewFullHistory, showToast }: {
  itemId: string
  onClose: () => void
  onAction: (action: ItemDrawerAction, item: StockItem) => void
  onViewFullHistory: (itemId: string, itemName: string) => void
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const { formatCurrency } = useCurrency()
  const [historyPage, setHistoryPage] = useState(1)
  const kitchenFlow = useKitchenFlowEnabled()

  const { data: itemRes, isLoading } = useQuery({
    queryKey: ['stockItem', itemId],
    queryFn: () => apiClient.getStockItem(itemId),
  })
  const item: StockItem | undefined = itemRes?.data

  const { data: histRes, isLoading: histLoading } = useQuery({
    queryKey: ['stockMovements', { item_id: itemId, page: historyPage }],
    queryFn: () => apiClient.getStockMovements({ item_id: itemId, page: historyPage, per_page: 15 }),
    placeholderData: (prev) => prev,
  })
  const history: StockMovement[] = histRes?.data ?? []
  const histMeta = histRes?.meta

  const status = item ? itemStatusMeta(item) : null
  const avgCost = item?.wac_unit_cost ?? item?.default_unit_cost
  const primary = item ? contextAction(item) : 'restock'

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose() }}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] overflow-y-auto">
        {isLoading || !item ? (
          <SheetHeader>
            <SheetTitle>Loading…</SheetTitle>
            <SheetDescription>Fetching item details.</SheetDescription>
          </SheetHeader>
        ) : (
          <>
            <SheetHeader>
              <div className="flex items-start justify-between gap-3 pr-8">
                <div className="min-w-0">
                  <SheetTitle className="truncate">{item.name}</SheetTitle>
                  <SheetDescription className="flex flex-wrap items-center gap-2 mt-1">
                    <span>{item.category?.name ?? 'Uncategorized'} · stocked by the {item.unit}</span>
                    {!item.is_active && <Badge variant="outline" className="text-xs">Archived</Badge>}
                  </SheetDescription>
                </div>
                <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => onAction('editItem', item)}>
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
              </div>
            </SheetHeader>

            <SheetBody className="space-y-5">
              {/* Status */}
              <div className="rounded-lg border p-4">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p
                      className={`text-3xl font-bold tabular-nums ${status?.qtyCls ?? ''}`}
                      title={isNearIntegerArtifact(item.quantity_on_hand) ? QTY_ARTIFACT_HINT : undefined}
                    >
                      {fmtQtyUnit(item.quantity_on_hand, item.unit)}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      {status && (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${status.chipCls}`}>
                          {status.label}
                        </span>
                      )}
                      {(item.on_order_qty ?? 0) > 0 && (
                        <span className="text-xs text-muted-foreground">{fmtQty(item.on_order_qty!)} {item.unit} on order</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    Warn at
                    <ReorderLevelPopover item={item} showToast={showToast} />
                  </div>
                </div>
                {item.severity === 'oversold' && (
                  <p className="mt-3 rounded-md bg-danger-surface/50 px-3 py-2 text-xs text-danger-fg">
                    The system says less than zero — impossible on a real shelf. Count the item to set it right in one step.
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" className="h-8" variant={primary === 'count' ? 'default' : 'outline'} onClick={() => onAction('count', item)}>
                    <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Count
                  </Button>
                  <Button size="sm" className="h-8" variant={primary === 'restock' ? 'default' : 'outline'} onClick={() => onAction('purchase', item)}>
                    <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Restock
                  </Button>
                  <Button
                    size="sm" variant="outline" className="h-8"
                    disabled={item.quantity_on_hand <= 0}
                    title={item.quantity_on_hand <= 0 ? 'Nothing on hand — record a count instead' : undefined}
                    onClick={() => onAction('issue', item)}
                  >
                    <ArrowUpCircle className="w-3.5 h-3.5 mr-1.5" /> Issue
                  </Button>
                  <Button
                    size="sm" variant="outline" className="h-8"
                    disabled={item.quantity_on_hand <= 0}
                    title={item.quantity_on_hand <= 0 ? 'No stock on hand to return' : undefined}
                    onClick={() => onAction('supplierReturn', item)}
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Return
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => onAction('adjust', item)}>
                    <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> Adjust
                  </Button>
                </div>
              </div>

              {/* Costs */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-lg font-semibold tabular-nums">{avgCost != null ? formatCurrency(avgCost) : '—'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Average cost — what recipes and profit use</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-lg font-semibold tabular-nums">{item.default_unit_cost != null ? formatCurrency(item.default_unit_cost) : '—'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Last paid — the newest purchase price</p>
                </div>
              </div>

              {/* Kitchen flow lane D: min/max per location and what we paid. */}
              {kitchenFlow && (
                <>
                  <ParLevelsCard itemId={item.id} unit={item.unit} showToast={showToast} />
                  <PriceHistoryCard itemId={item.id} unit={item.unit} />
                </>
              )}

              {/* Used in (F-MOD-N11): the recipes that draw on this item, each a
                  link to its costing sheet for roles that can open it.
                  Answers "why does this deduct?" */}
              <UsedIn itemId={item.id} unit={item.unit} />

              {/* Kitchen flow (Lane A): barcodes and the buying unit. */}
              {kitchenFlow && (
                <>
                  <PurchaseUnitLine item={item} />
                  <ItemBarcodesSection item={item} showToast={showToast} />
                </>
              )}
              {/* Lots, for every tenant (F-CTRL-N73). */}
              <ItemLotsPanel item={item} showToast={showToast} />
              {/* Kitchen flow lane B: prep recipe + production. Renders nothing
                  unless the tenant has FEATURE_KITCHEN_FLOW. */}
              <PrepSection item={item} showToast={showToast} />

              {(item.supplier_name || item.notes || item.tracks_expiry) && (
                <div className="rounded-lg border p-3 space-y-1 text-sm">
                  {item.supplier_name && <p><span className="text-muted-foreground">Vendor:</span> {item.supplier_name}</p>}
                  {item.tracks_expiry && (
                    <p>
                      <span className="text-muted-foreground">Expiry:</span> tracked
                      {item.shelf_life_days ? `, ${item.shelf_life_days}-day shelf life` : ''}
                    </p>
                  )}
                  {item.notes && <p className="text-muted-foreground">{item.notes}</p>}
                </div>
              )}

              {/* History */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-semibold">History</h4>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onViewFullHistory(item.id, item.name)}>
                    View all in History
                  </Button>
                </div>
                {histLoading && history.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">Loading history…</p>
                ) : history.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">No recorded movements yet.</p>
                ) : (
                  <ul className="divide-y rounded-lg border">
                    {history.map(m => {
                      const d = describeMovement(m)
                      const voided = Boolean(m.voided_at)
                      return (
                        <li key={m.id} className={`px-3 py-2 ${voided ? 'opacity-60' : ''}`}>
                          <div className="flex items-baseline justify-between gap-3">
                            <span className={`text-sm font-medium truncate ${voided ? 'line-through' : ''} ${MOVEMENT_TONE_CLS[d.tone]}`}>
                              {d.title}
                            </span>
                            <span
                              className={`text-sm tabular-nums whitespace-nowrap ${voided ? 'line-through' : ''} ${m.quantity < 0 ? 'text-muted-foreground' : 'text-success-fg'}`}
                              title={isNearIntegerArtifact(m.quantity) ? QTY_ARTIFACT_HINT : undefined}
                            >
                              {m.quantity > 0 ? '+' : ''}{fmtQtyUnit(m.quantity, m.item_unit ?? item.unit)}
                            </span>
                          </div>
                          <div className="mt-0.5 flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
                            <span className="truncate">
                              {formatDateTimeHuman(m.created_at)}
                              {m.created_by_name ? ` · ${m.created_by_name}` : ''}
                              {voided && m.void_reason ? ` · Voided${m.voided_by_name ? ` by ${m.voided_by_name}` : ''}: ${m.void_reason}` : ''}
                            </span>
                            {m.total_cost != null && <span className="tabular-nums whitespace-nowrap">{formatCurrency(m.total_cost)}</span>}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                )}
                {histMeta && histMeta.total_pages > 1 && (
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Page {historyPage} of {histMeta.total_pages}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7" disabled={historyPage <= 1} onClick={() => setHistoryPage(p => p - 1)}>Prev</Button>
                      <Button size="sm" variant="outline" className="h-7" disabled={historyPage >= histMeta.total_pages} onClick={() => setHistoryPage(p => p + 1)}>Next</Button>
                    </div>
                  </div>
                )}
              </div>
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

/**
 * Audit U-19: the count is distinct recipes (one recipe can hold the same item
 * on a dine-in row and a takeaway row), quantities carry the stock unit, and a
 * name only links for roles that can open Menu Management. The inventory
 * manager, whose only screen this is, gets plain names instead of a link that
 * bounces them.
 */
function UsedIn({ itemId, unit }: { itemId: string; unit: string }) {
  const { data } = useQuery({
    queryKey: ['stock-item-used-in', itemId],
    queryFn: () => apiClient.getStockItemUsedIn(itemId).then((r) => r.data ?? []),
    retry: false,
  })
  const rows = data ?? []
  if (rows.length === 0) return null
  const recipes = new Set(rows.map((u) => (u.kind === 'product' ? `p:${u.product_id}` : `o:${u.option_id}`))).size
  const canLink = canAccessAdminRoute(getStoredUserRole(), '/admin/menu')
  return (
    <div className="rounded-lg border p-3">
      <h4 className="mb-1.5 text-sm font-semibold">Used in {recipes} recipe{recipes === 1 ? '' : 's'}</h4>
      <ul className="space-y-1 text-sm">
        {rows.map((u, i) => (
          <li key={`${u.kind}-${u.product_id ?? u.option_id}-${u.order_type_scope}-${i}`} className="flex items-center justify-between gap-2">
            {u.kind === 'product' ? (
              canLink ? (
                <Link
                  to="/admin/menu"
                  search={{ tab: 'costing', item: u.product_id }}
                  className="truncate underline-offset-2 hover:underline"
                >
                  {u.product_name}
                </Link>
              ) : (
                <span className="truncate">{u.product_name}</span>
              )
            ) : (
              <span className="truncate">
                {u.option_name} <span className="text-muted-foreground">({u.group_name} option)</span>
              </span>
            )}
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {fmtQty(u.deduct_qty)} {unit} per serving{u.order_type_scope === 'takeaway' ? ' · takeaway only' : u.order_type_scope === 'dine_in' ? ' · dine-in only' : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
